import { useState } from 'react';
import { Linkedin, Share2 } from 'lucide-react';
import { linkedInPostUrl } from '@/lib/linkedin';
import { Button } from './ui';

/*
 * Share buttons that work on phones too. LinkedIn's composer takes the
 * ready-made post only on desktop: its app and mobile site ignore the text.
 * So phones get one "Share" button instead, the phone's own share sheet with
 * the post (LinkedIn, WhatsApp, Messages…). A phone without one keeps "Share
 * on LinkedIn", which copies the post and opens LinkedIn's share-a-link screen.
 */

/** Phones and tablets, where LinkedIn opens its app or mobile site. */
export const isPhone = () =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac.
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** LinkedIn's share-a-link screen: a new post with the link's preview attached. */
const linkedInShareLinkUrl = (url: string) =>
  `https://www.linkedin.com/sharing/share-offsite/?${new URLSearchParams({ url })}`;

const primary =
  'inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius)] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90';

/** Phones whose browser has a share sheet: they get "Share" instead of LinkedIn's button. */
const hasShareSheet = () => isPhone() && typeof navigator.share === 'function';

/**
 * "Share on LinkedIn" with `post` filled in on desktop. Hidden on phones with
 * a share sheet; on other phones it copies the post for pasting.
 */
export function LinkedInShareButton({ post, url }: { post: string; url: string }) {
  const [phone] = useState(isPhone);
  const [hidden] = useState(hasShareSheet);
  const [copied, setCopied] = useState(false);
  if (hidden) return null;
  return (
    <div className="flex flex-col gap-1">
      <a
        href={phone ? linkedInShareLinkUrl(url) : linkedInPostUrl(post)}
        target="_blank"
        rel="noopener noreferrer"
        className={primary}
        onClick={() => {
          if (!phone) return;
          // Started inside the tap, so phones allow it; LinkedIn opens meanwhile.
          void navigator.clipboard
            ?.writeText(post)
            .then(() => setCopied(true))
            .catch(() => {});
        }}
      >
        <Linkedin className="size-4" aria-hidden /> Share on LinkedIn
      </a>
      {copied && (
        <p role="status" className="text-xs text-muted-foreground">
          Post text copied: paste it into your LinkedIn post.
        </p>
      )}
    </div>
  );
}

/** "Share": the phone's share sheet, where there is one (desktop has the LinkedIn button). */
export function NativeShareButton({ title, text }: { title: string; text: string }) {
  const [available] = useState(hasShareSheet);
  if (!available) return null;
  return (
    <Button
      onClick={() => {
        // The post already ends with the link; a separate url would repeat it.
        navigator.share({ title, text }).catch(() => {});
      }}
    >
      <Share2 className="size-4" aria-hidden /> Share
    </Button>
  );
}
